import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { css, StyleSheet } from 'aphrodite/no-important';
import WarningDialog from '../warning-dialog';
import {
  PopupMenu,
  TextInput,
  DialogBox,
  DatePicker,
  TimePicker,
  Button
} from 'quantux-ui';
import { BASE_URL, titleCase, padZero } from '../../utils';
import { gray, blue, yellow, salmon } from '../../theme';

const TODAY = new Date();
const DIALOG_WIDTH = window.innerWidth <= 576 ? (window.innerWidth - 16) : 350;

const GanttCell = (props) => {
  const [showSyncWarning, setShowSyncWarning] = useState(false);
  const [isEditCaseBlockAnes, setIsEditCaseBlockAnes] = useState(false);
  const [isEditCaseBlockCrna, setIsEditCaseBlockCrna] = useState(false);
  // Anesthesiologist search
  const [showAnesMenu, setShowAnesMenu] = useState(false);
  // CRNA search
  const [showCrnaMenu, setShowCrnaMenu] = useState(false);
  // Edit Assignment
  const [isEditAssignment, setIsEditAssignment] = useState(false);
  const [providerSearch, setProviderSearch] = useState('');
  const [providerSearchResults, setProviderSearchResults] = useState([]);
  const [isProviderSearchPristine, setIsProviderSearchPristine] = useState(true);
  const [showProviderMenu, setShowProviderMenu] = useState(false);
  const [providerDialogTop, setProviderDialogTop] = useState(null);
  const [providerDialogLeft, setProviderDialogLeft] = useState(null);
  // Assignment dates
  const [blockTitle, setBlockTitle] = useState('');
  const [assignmentStartDate, setAssignmentStartDate] = useState(TODAY.getDate());
  const [assignmentStartMonth, setAssignmentStartMonth] = useState(TODAY.getMonth());
  const [assignmentStartYear, setAssignmentStartYear] = useState(TODAY.getFullYear());
  const [isAssignmentStartDatePristine, setIsAssignmentStartDatePristine] = useState(true);
  const [assignmentStartHour, setAssignmentStartHour] = useState(TODAY.getHours());
  const [assignmentStartMinute, setAssignmentStartMinute] = useState(TODAY.getMinutes());
  const [isAssignmentStartTimePristine, setIsDirBlockStartTimePristine] = useState(true);
  const [assignmentStopDate, setAssignmentStopDate] = useState(TODAY.getDate());
  const [assignmentStopMonth, setAssignmentStopMonth] = useState(TODAY.getMonth());
  const [assignmentStopYear, setAssignmentStopYear] = useState(TODAY.getFullYear());
  const [isAssignmentStopDatePristine, setIsAssignmentStopDatePristine] = useState(true);
  const [assignmentStopHour, setAssignmentStopHour] = useState(TODAY.getHours());
  const [assignmentStopMinute, setAssignmentStopMinute] = useState(TODAY.getMinutes());
  const [isAssignmentStopTimePristine, setIsDirBlockStopTimePristine] = useState(true);
  const [assignmentProviderStr, setAssignmentProviderStr] = useState('');
  const [assignmentProvider, setAssignmentProvider] = useState('');
  // --
  const [showRepeatMenu, setShowRepeatMenu] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState('none');
  const [selectedProvider, setSelectedProvider] = useState({});
  // -- Edit case block (Anes)
  const [showCaseblockProviderWarning, setShowCaseblockProviderWarning] = useState(false);
  const [showCaseblockCrnaWarning, setShowCaseblockCrnaWarning] = useState(false);
  const [caseBlockAnes, setCaseBlockAnes] = useState({});
  const [caseBlockAnesStr, setCaseBlockAnesStr] = useState('');
  const [cbAnesSearch, setCbAnesSearch] = useState('');
  const [cbAnesSearchResults, setCbAnesSearchResults] = useState([]);
  const [isCbAnesSearchPristine, setIsCbAnesSearchPristine] = useState(true);
  const [anesDialogLeft, setAnesDialogLeft] = useState(null);
  const [anesDialogTop, setAnesDialogTop] = useState(null);
  // -- Edit case block (CRNA)
  const [caseBlockCrna, setCaseBlockCrna] = useState({});
  const [caseBlockCrnaStr, setCaseBlockCrnaStr] = useState('');
  const [cbCrnaSearch, setCbCrnaSearch] = useState('');
  const [cbCrnaSearchResults, setCbCrnaSearchResults] = useState([]);
  const [isCbCrnaSearchPristine, setIsCbCrnaSearchPristine] = useState(true);
  const [crnaDialogLeft, setCrnaDialogLeft] = useState(null);
  const [crnaDialogTop, setCrnaDialogTop] = useState(null);

  // Props
  const {
    id,
    left,
    width,
    position,
    surgStr,
    dirStr,
    cbCasesData,
    anesStr,
    practiceId,
    crnaStr
  } = props;

  useEffect(() => {
    if (providerRef.current) {
      const left = providerRef.current.getBoundingClientRect().left;
      const top = providerRef.current.getBoundingClientRect().top;
      setProviderDialogLeft(Math.max(0, left - 6));
      setProviderDialogTop(top + 35);
    }
  }, [isEditAssignment]);

  useEffect(() => {
    if (anesRef.current) {
      const left = anesRef.current.getBoundingClientRect().left;
      const top = anesRef.current.getBoundingClientRect().top;
      setAnesDialogLeft(Math.max(0, left - 6));
      setAnesDialogTop(top + 35);
    }
  }, [isEditCaseBlockAnes]);

  useEffect(() => {
    if (crnaRef.current) {
      const left = crnaRef.current.getBoundingClientRect().left;
      const top = crnaRef.current.getBoundingClientRect().top;
      setCrnaDialogLeft(Math.max(0, left - 6));
      setCrnaDialogTop(top + 35);
    }
  }, [isEditCaseBlockCrna]);

  // --- Refs
  const anesMenuRef = useRef();
  const crnaMenuRef = useRef();
  const providerMenuRef = useRef();
  const providerRef = useRef();
  const anesRef = useRef();
  const crnaRef = useRef();
  const repeatMenuRef = useRef();

  // ____ Network Ops ________________________________________________
  // --- CRNA/Practice Search ---
  const searchCrnas = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedProsCrnas`);
    const params = {
      searchTerm: value.trim(),
      enforcePracticeRelation: true,
      practiceId
    };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setCbCrnaSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedCrnasSearch = useCallback(debounce(value => searchCrnas(value), 800), []);

  const searchAnesthesiologists = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedProsAnesthesiologists`);
    const params = {
      searchTerm: value.trim(),
      enforcePracticeRelation: true,
      practiceId
    };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setCbAnesSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedAnesSearch = useCallback(debounce(value => searchAnesthesiologists(value), 800), []);

  const searchProviders = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedProsAnesthesiologists`);
    const params = {
      searchTerm: value.trim(),
      enforcePracticeRelation: true,
      practiceId
    };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setProviderSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedProviderSearch = useCallback(debounce(value => searchProviders(value), 800), []);

  // --- Styles
  const styles = StyleSheet.create({
    ganttCell: {
      position: 'absolute',
      left: `${left}px`,
      top: position > 0 ? `${position * (props.height - 2)}px` : '0',
      width: `${width}px`,
      background: blue[700],
      color: gray[200],
      height: `${props.height - 2}px`,
      fontSize: '11px',
      padding: '0 4px',
      lineHeight: '16px',
      borderRight: `1px solid ${gray[900]}`,
      borderTop: `1px solid ${gray[900]}`
    },
    gcTopRow: {
      justifyContent: 'space-between',
      paddingTop: '4px',
      height: '18px'
    },
    gcTopRowText: {
      width: `${width - 28}px`,
      maxWidth: `${width - 28}px`
    },
    sideDrawerHeaderRow: {
      display: 'flex',
      marginTop: '16px',
      width: '100%'
    },
    sideDrawerHeaderRowTitle: {
      minWidth: '60px',
      marginRight: '8px',
      textAlign: 'right'
    },
    numberOfCasesText: {
      color: 'black',
      fontSize: '12px',
      fontWeight: '700',
      position: 'absolute',
      top: '0',
      left: '0',
      paddingTop: '1px',
      minWidth: '16px',
      minHeight: '16px'
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      background: '#103A60',
      width: '220px',
      maxWidth: '220px',
      minHeight: '80px',
      fontSize: '14px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '100%',
      paddingLeft: '8px',
      background: '#103A60',
      ':not(.active):hover': {
        background: '#0066CC',
        color: 'white'
      }
    },
    gridEllipsisMenuItemActive: {
      background: gray[700],
      color: 'white'
    }
  });

  // --- Event Handlers
  const handleOnChangeAnesSearch = (e) => {
    const { value } = e.target;
    setIsCbAnesSearchPristine(false);
    setCbAnesSearch(value);
    if (value.trim().length > 0) {
      debouncedAnesSearch(value);
    }
  };

  const handleOnChangeCrnaSearch = (e) => {
    const { value } = e.target;
    setIsCbCrnaSearchPristine(false);
    setCbCrnaSearch(value);
    if (value.trim().length > 0) {
      debouncedCrnasSearch(value);
    }
  };

  const handleOnChangeProviderSearch = (e) => {
    const { value } = e.target;
    setIsProviderSearchPristine(false);
    setProviderSearch(value);
    if (value.trim().length > 0) {
      debouncedProviderSearch(value);
    }
  };

  const handleOnEditAssignment = () => {
    const {
      blockTitle = 'Assignment',
      blockType = '',
      director,
      provider,
      repeatFrequency,
      startDate,
      startHour,
      startMinute,
      stopDate,
      stopHour,
      stopMinute
    } = props.dbCasesData;

    const tmpStartDate = new Date(Date.parse(startDate));
    const tmpStopDate = new Date(Date.parse(stopDate));

    setAssignmentProviderStr(dirStr);
    if (blockType === 'director') {
      const { fullName } = director;
      const fullNameParts = fullName.split(',');
      const firstName = fullNameParts[1] ? fullNameParts[1].trim() : '';
      const lastName = fullNameParts[0] ? fullNameParts[0].trim() : '';
      setAssignmentProvider({
        ...director,
        firstName,
        lastName
      });
    } else {
      const fullName = provider.fullName;
      const fullNameParts = fullName.split(',');
      const firstName = fullNameParts[1] ? fullNameParts[1].trim() : '';
      const lastName = fullNameParts[0] ? fullNameParts[0].trim() : '';
      setAssignmentProvider({
        ...provider,
        firstName,
        lastName
      });
    }
    setAssignmentStartDate(tmpStartDate.getDate());
    setAssignmentStartMonth(tmpStartDate.getMonth());
    setAssignmentStartYear(tmpStartDate.getFullYear());
    setIsAssignmentStartDatePristine(false);
    setAssignmentStartHour(startHour);
    setAssignmentStartMinute(startMinute);
    setIsDirBlockStartTimePristine(false);

    setAssignmentStopDate(tmpStopDate.getDate());
    setAssignmentStopMonth(tmpStopDate.getMonth());
    setAssignmentStopYear(tmpStopDate.getFullYear());
    setIsAssignmentStopDatePristine(false);
    setAssignmentStopHour(stopHour);
    setAssignmentStopMinute(stopMinute);
    setIsDirBlockStopTimePristine(false);
    setBlockTitle(blockTitle);
    setRepeatFrequency(repeatFrequency);

    setIsEditAssignment(true);
  };

  const handleOnUpdateAssignment = () => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    // Form Data
    const formData = new FormData();
    formData.append('blockId', props.dbCasesData._id);
    formData.append('startHour', assignmentStartHour);
    formData.append('startMinute', assignmentStartMinute);
    formData.append('startDate', `${assignmentStartYear}-${padZero(assignmentStartMonth + 1)}-${padZero(assignmentStartDate)}T00:00:00.000Z`);
    formData.append('stopHour', assignmentStopHour);
    formData.append('stopMinute', assignmentStopMinute);
    formData.append('stopDate', `${assignmentStopYear}-${padZero(assignmentStopMonth + 1)}-${padZero(assignmentStopDate)}T00:00:00.000Z`);
    formData.append('isRepeat', JSON.stringify(repeatFrequency.toLowerCase() !== 'none'));
    formData.append('repeatFrequency', repeatFrequency);

    // Req Options
    const requestOptions = { method: 'POST', headers: myHeaders, body: formData, redirect: 'follow' };

    let url = '';
    switch (props.dbCasesData.blockType) {
      case 'director':
        url = `${BASE_URL}/directorBlock/updateDirectorBlock`;
        formData.append('director', JSON.stringify({
          avatarImageUrl: assignmentProvider.avatarImageUrl,
          caseRelation: 'Director',
          credentials: assignmentProvider.credentials,
          fullName: `${assignmentProvider.lastName}, ${assignmentProvider.firstName}`,
          icon: assignmentProvider.avatarImageUrl,
          npi: assignmentProvider.npi,
          text: `${assignmentProvider.lastName}, ${assignmentProvider.firstName}`,
          user: assignmentProvider._id || assignmentProvider.user
        }));
        break;
      case 'shift':
        url = `${BASE_URL}/shiftBlock/updateShiftBlock`;
        formData.append('provider', JSON.stringify({
          avatarImageUrl: assignmentProvider.avatarImageUrl,
          caseRelation: 'Provider',
          credentials: assignmentProvider.credentials,
          fullName: `${assignmentProvider.lastName}, ${assignmentProvider.firstName}`,
          icon: assignmentProvider.avatarImageUrl,
          npi: assignmentProvider.npi,
          text: `${assignmentProvider.lastName}, ${assignmentProvider.firstName}`,
          user: assignmentProvider._id || assignmentProvider.user
        }));
        break;
      case 'call':
        url = `${BASE_URL}/callBlock/updateCallBlock`;
        formData.append('provider', JSON.stringify({
          avatarImageUrl: assignmentProvider.avatarImageUrl,
          caseRelation: 'Provider',
          credentials: assignmentProvider.credentials,
          fullName: `${assignmentProvider.lastName}, ${assignmentProvider.firstName}`,
          icon: assignmentProvider.avatarImageUrl,
          npi: assignmentProvider.npi,
          text: `${assignmentProvider.lastName}, ${assignmentProvider.firstName}`,
          user: assignmentProvider._id || assignmentProvider.user
        }));
        break;
      default:
        url = '';
    }
    fetch(url, requestOptions)
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        if (data.success) {
          props.onReloadData();
        }
        setIsEditAssignment(false);
      })
      .catch(err => console.log(err));
  };

  const handleOnEditCaseBlockAnes = () => {
    setShowCaseblockProviderWarning(false);
    setIsEditCaseBlockAnes(true);
    setCaseBlockAnes(props.cbCasesData.anesthesiologist || {});
    if (props.cbCasesData.anesthesiologist && Object.keys(props.cbCasesData.anesthesiologist).length > 0) {
      const anesFullName = props.cbCasesData.anesthesiologist.fullName;
      const tmp = anesFullName.split(',');
      const anesLastName = titleCase(tmp[0].trim());
      const anesFirstName = tmp[1].trim().charAt(0).toUpperCase()
      setCaseBlockAnesStr(`${anesFirstName}. ${anesLastName}`);
    }
  };

  const handleOnEditCaseBlockCrna = () => {
    setShowCaseblockCrnaWarning(false);
    setIsEditCaseBlockCrna(true);
    setCaseBlockCrna(props.cbCasesData.crna || {});
    if (props.cbCasesData.crna && Object.keys(props.cbCasesData.crna).length > 0) {
      const crnaFullName = props.cbCasesData.crna.fullName;
      const tmp = crnaFullName.split(',');
      const crnaLastName = titleCase(tmp[0].trim());
      const crnaFirstName = tmp[1].trim().charAt(0).toUpperCase()
      setCaseBlockCrnaStr(`${crnaFirstName}. ${crnaLastName}`);
    }
  };

  return (
    <>
      {showSyncWarning &&
        <WarningDialog
          show={showSyncWarning}
          title='Sync Case Block Providers'
          message='Do you want to sync all providers in the case block?'
          onCancel={() => setShowSyncWarning(false)}
          onContinue={() => {
            setShowSyncWarning(false);
          }}
        />
      }
      <div className={css(styles.ganttCell)}>
        {/* Top Row */}
        {props.type === 'case' &&
          <div className={`fx ${css(styles.gcTopRow)}`}>
            <div className={`text-truncate ${css(styles.gcTopRowText)}`}>
              {surgStr}
            </div>
            {props.type === 'case' &&
              <div className='fx'>
                <div className='ml-1' style={{ position: 'relative' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="none"
                  >
                    <path fill="#96C8FF" fillRule="evenodd" d="M11.2 3.368h3.2c.888 0 1.6.75 1.6 1.685v9.263C16 15.25 15.288 16 14.4 16H1.6C.712 16 0 15.25 0 14.316l.008-9.263c0-.935.704-1.685 1.592-1.685h3.2V1.684C4.8.75 5.512 0 6.4 0h3.2c.888 0 1.6.75 1.6 1.684v1.684Zm-4.8-.164h3.2v-1.6H6.4v1.6Z" clipRule="evenodd" />
                  </svg>
                  <div
                    className={`fx fx__hv-center ${css(styles.numberOfCasesText)}`}
                    onClick={() => {
                      props.onEditCaseBlock(cbCasesData);
                    }}
                  >
                    {cbCasesData.cases && Array.isArray(cbCasesData.cases) ? cbCasesData.cases.length : 0}
                  </div>
                </div>
              </div>
            }
          </div>
        }
        {/* Anes Row */}
        {props.type === 'case' &&
          <div
            className='fx'
            style={{ paddingTop: '2px' }}
            ref={anesRef}
          >
            <div>Anes</div>
            <div
              className='ml-2'
              // style={{ color: anesSearch !== 'None' ? blue[400] : yellow[500] }}
              style={{ color: blue[400] }}
              onClick={handleOnEditCaseBlockAnes}
            >
              {anesStr !== 'None' ? anesStr : 'None'}
              <i
                className='ml-1 fa fa-chevron-down'
                onClick={handleOnEditCaseBlockAnes}
              />
            </div>
          </div>
        }
        {/* CRNA Row */}
        {props.type === 'case' &&
          <div
            className='fx'
            ref={crnaRef}
          >
            <div>CRNA</div>
            <div
              className='ml-2'
              // style={{ color: crnaStr !== 'None' ? blue[400] : yellow[500] }}
              style={{ color: blue[400] }}
              onClick={handleOnEditCaseBlockCrna}
            >
              {crnaStr !== 'None' ? crnaStr : 'None'}
              <i
                className='ml-1 fa fa-chevron-down'
                onClick={handleOnEditCaseBlockCrna}
              />
            </div>
          </div>
        }
        {/* Assignment Cells */}
        {/* Director / Call Provider / Shift Provider */}
        {props.type === 'assignment' &&
          <div
            className={`fx pl-1 ${css(styles.gcTopRow)}`}
            ref={providerRef}
          >
            <div
              ref={providerMenuRef}
              style={{
                color: blue[400],
                position: 'relative'
              }}
            >
              {dirStr}
            </div>
            <div onClick={handleOnEditAssignment}>
              <i className='fa fa-pencil' />
            </div>
          </div>
        }
        {/* Director / Shift / Call cells */}
        {props.type === 'assignment' &&
          <div className='pl-1 fx'>
            <div>
              {`${props.startHour}${props.startMinute}`}
            </div>
            <div className='ml-2 mr-2'>-</div>
            <div>
              {`${props.stopHour}${props.stopMinute}`}
            </div>
          </div>
        }
        {/* Director/Shift/Call edit mode */}
        {props.type === 'assignment' && isEditAssignment &&
          <DialogBox
            showCloseBtn={false}
            initWidth={DIALOG_WIDTH}
            top={window.innerWidth <= 576 ? 48 : providerDialogTop}
            left={window.innerWidth <= 576 ? 8 : providerDialogLeft}
            initHeight={265}
            isOpen={isEditAssignment}
            onClose={() => {
              setIsEditAssignment(false);
            }}
            maskBackground="#004080"
            maskOpacity={0}
            closeOnEscKey={false}
            closeOnMaskClick={false}
            header={{
              overrideTheme: true,
              text: `Edit ${blockTitle}`,
              backColor: '#082840',
              foreColor: blue[400],
              closeColor: '#FF6060',
              fontStyle: 'bold',
              fontSize: '14px',
              icon: 'fa fa-pencil'
            }}
          >
            <div
              className='pb-2'
              style={{ minHeight: '120px' }}
            >
              <div className='pl-3 pr-3 pt-2'>
                {/* Assignment Provider */}
                <div className='fx'>
                  <div className={css(styles.sideDrawerHeaderRowTitle)}>
                    Provider
                  </div>
                  <div
                    className='ml-2'
                    style={{
                      color: blue[400],
                      position: 'relative'
                    }}
                  >
                    {assignmentProviderStr}
                    <i
                      className='ml-1 fa fa-chevron-down'
                      onClick={() => {
                        setProviderSearch('');
                        setProviderSearchResults([]);
                        setIsProviderSearchPristine(true);
                        setShowProviderMenu(!showProviderMenu);
                      }}
                    />
                    {/* Anestehsiologist serach menu */}
                    {showProviderMenu &&
                      <PopupMenu
                        top={1}
                        left={1}
                        position='absolute'
                        zIndex='3000'
                        visible={showProviderMenu}
                        parent={providerMenuRef}
                        onOutsideClick={() => {
                          setShowProviderMenu(false);
                        }}
                      >
                        <div className={css(styles.gridEllipsisMenuContainer)}>
                          {/* Provider search textbox */}
                          <div className='pl-2 pt-1'>
                            <TextInput
                              placeholder='Search Provider'
                              value={providerSearch}
                              onChange={handleOnChangeProviderSearch}
                              style={{
                                width: '204px',
                                height: '20px',
                                background: '#103a60',
                                border: '1px solid #60A0E0',
                                borderWidth: '0 0 1px 0',
                                marginBottom: '2px',
                                fontSize: '12px',
                                fontWeight: '700'
                              }}
                            />
                          </div>
                          {providerSearchResults.length === 0 && isProviderSearchPristine &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              Type provider name in the text box above to search
                            </div>
                          }
                          {providerSearchResults.length === 0 && !isProviderSearchPristine && providerSearch.length === 0 &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              Type provider name in the text box above to search
                            </div>
                          }
                          {providerSearchResults.length === 0 && !isProviderSearchPristine && providerSearch.length > 0 &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              No results found
                            </div>
                          }
                          {providerSearchResults.map((provider) => {
                            return (
                              <div
                                key={`${provider.npi}__surg`}
                                className={css(styles.gridEllipsisMenuItem)}
                                onClick={() => {
                                  // props.onUpdateBlockAnes(anes, id);
                                  setShowProviderMenu(false);
                                  setSelectedProvider(provider);
                                  setAssignmentProviderStr(`${provider.firstName.trim().charAt(0).toUpperCase()}. ${titleCase(provider.lastName.trim())}`);
                                }}
                              >
                                {`${provider.lastName}, ${provider.firstName}`}
                              </div>
                            );
                          })}
                        </div>
                      </PopupMenu>
                    }
                  </div>
                </div>
                {/* Assignment start date and time */}
                <div className={css(styles.sideDrawerHeaderRow)}>
                  <div className={css(styles.sideDrawerHeaderRowTitle)}>
                    Start
                  </div>
                  <DatePicker
                    className='ml-2'
                    left={-10}
                    date={assignmentStartDate}
                    month={assignmentStartMonth}
                    year={assignmentStartYear}
                    isPrisitine={isAssignmentStartDatePristine}
                    color={isAssignmentStartDatePristine ? yellow[500] : blue[400]}
                    noUnderline
                    onChangeDate={(year, month, date) => {
                      setAssignmentStartDate(date);
                      setAssignmentStartMonth(month);
                      setAssignmentStartYear(year);
                      setIsAssignmentStartDatePristine(false);
                    }}
                  />
                  <TimePicker
                    className='ml-2 pl-1 pr-2'
                    hour={assignmentStartHour}
                    minute={assignmentStartMinute}
                    color={isAssignmentStartTimePristine ? yellow[500] : blue[500]}
                    noUnderline
                    timeFormat='24H'
                    timePeriod='AM'
                    isPristine={isAssignmentStartTimePristine}
                    onChangeTime={(hour, min) => {
                      setIsAssignmentStartDatePristine(false);
                      setAssignmentStartHour(hour);
                      setAssignmentStartMinute(min);
                    }}
                    disableTimeFormat
                  />
                </div>
                {/* Assignment stop date and time */}
                <div className={css(styles.sideDrawerHeaderRow)}>
                  <div className={css(styles.sideDrawerHeaderRowTitle)}>
                    Stop
                  </div>
                  <DatePicker
                    className='ml-2'
                    left={-10}
                    date={assignmentStopDate}
                    month={assignmentStopMonth}
                    year={assignmentStopYear}
                    isPrisitine={isAssignmentStopDatePristine}
                    color={isAssignmentStopDatePristine ? yellow[500] : blue[400]}
                    noUnderline
                    onChangeDate={(year, month, date) => {
                      setAssignmentStopDate(date);
                      setAssignmentStopMonth(month);
                      setAssignmentStopYear(year);
                      setIsAssignmentStopDatePristine(false);
                    }}
                  />
                  <TimePicker
                    className='ml-2 pl-1 pr-2'
                    hour={assignmentStopHour}
                    minute={assignmentStopMinute}
                    color={isAssignmentStopTimePristine ? yellow[500] : blue[500]}
                    noUnderline
                    timeFormat='24H'
                    timePeriod='AM'
                    isPristine={isAssignmentStopTimePristine}
                    onChangeTime={(hour, min) => {
                      setIsAssignmentStopDatePristine(false);
                      setAssignmentStopHour(hour);
                      setAssignmentStopMinute(min);
                    }}
                    disableTimeFormat
                  />
                </div>
                {/* Repeat Assignments */}
                <div
                  style={{ position: 'relative' }}
                  className={`mb-2 ${css(styles.sideDrawerHeaderRow)}`}
                >
                  <div className={css(styles.sideDrawerHeaderRowTitle)}>
                    Repeat
                  </div>
                  <div
                    className='ml-2'
                    ref={repeatMenuRef}
                    onClick={() => setShowRepeatMenu(true)}
                  >
                    {titleCase(repeatFrequency) === 'None' && 'Never'}
                    {titleCase(repeatFrequency) === 'Week' && 'Every Week'}
                    {titleCase(repeatFrequency) === 'Month' && 'Every Month'}
                    {titleCase(repeatFrequency) === 'Year' && 'Every Year'}
                    <i className='ml-2 fa fa-chevron-down' />
                  </div>
                  <PopupMenu
                    top={20}
                    left={53}
                    position="absolute"
                    zIndex="2501"
                    visible={showRepeatMenu}
                    parent={repeatMenuRef}
                    onOutsideClick={() => { setShowRepeatMenu(false); }}
                  >
                    <div style={{ width: '120px', height: '64px', minHeight: '64px' }} className={css(styles.gridEllipsisMenuContainer)}>
                      {/* Every Week */}
                      <div
                        className={css(styles.gridEllipsisMenuItem, repeatFrequency.toLowerCase() === 'week' && styles.gridEllipsisMenuItemActive)}
                        onClick={() => {
                          setRepeatFrequency('week');
                          setShowRepeatMenu(false);
                        }}
                      >
                        Every Week
                      </div>
                      {/* Every Month */}
                      <div
                        className={css(styles.gridEllipsisMenuItem, repeatFrequency.toLowerCase() === 'month' && styles.gridEllipsisMenuItemActive)}
                        onClick={() => {
                          setRepeatFrequency('month');
                          setShowRepeatMenu(false);
                        }}
                      >
                        Every Month
                      </div>
                      {/* Every Year */}
                      <div
                        className={css(styles.gridEllipsisMenuItem, repeatFrequency.toLowerCase() === 'year' && styles.gridEllipsisMenuItemActive)}
                        onClick={() => {
                          setRepeatFrequency('year');
                          setShowRepeatMenu(false);
                        }}
                      >
                        Every Year
                      </div>
                    </div>
                  </PopupMenu>
                </div>
              </div>
              {/* Footer */}
              <div
                className='mt-4 pt-2 fx pr-2'
                style={{
                  height: '44px',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  borderTop: `1px solid ${gray[900]}`
                }}
              >
                {/* Cancel Button */}
                <Button
                  btnType='primary'
                  isOutlineBtn
                  style={{
                    border: `1px solid ${gray[400]}`,
                    color: gray[300],
                    height: '32px',
                    width: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => setIsEditAssignment(false)}
                >
                  Cancel
                </Button>
                {/* Save Button */}
                <Button
                  btnType='primary'
                  className='ml-2'
                  style={{
                    height: '32px',
                    width: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                  onClick={handleOnUpdateAssignment}
                >
                  Update
                </Button>
              </div>
            </div>
          </DialogBox>
        }
        {/* Case block - Anesthesiologist edit mode */}
        {isEditCaseBlockAnes &&
          <DialogBox
            showCloseBtn={false}
            initWidth={DIALOG_WIDTH}
            top={window.innerWidth <= 576 ? 48 : anesDialogTop}
            left={window.innerWidth <= 576 ? 8 : anesDialogLeft}
            initHeight={265}
            isOpen={isEditCaseBlockAnes}
            onClose={() => {
              setIsEditCaseBlockAnes(false);
            }}
            maskBackground="#004080"
            maskOpacity={0}
            closeOnEscKey={false}
            closeOnMaskClick={false}
            header={{
              overrideTheme: true,
              text: 'Edit Case Block',
              backColor: '#082840',
              foreColor: blue[400],
              closeColor: '#FF6060',
              fontStyle: 'bold',
              fontSize: '14px',
              icon: 'fa fa-pencil'
            }}
          >
            <div
              className='pb-2'
              style={{ minHeight: '120px' }}
            >
              <div className='pl-3 pr-3 pt-2'>
                {/* Case block Anesthesiologist */}
                <div
                  className='fx'
                  ref={anesMenuRef}
                >
                  <div className={css(styles.sideDrawerHeaderRowTitle)}>
                    Anes
                  </div>
                  <div
                    className='ml-2'
                    style={{
                      color: blue[400],
                      position: 'relative'
                    }}
                  >
                    {caseBlockAnesStr}
                    <i
                      className='ml-1 fa fa-chevron-down'
                      onClick={() => {
                        setCbAnesSearch('');
                        setCbAnesSearchResults([]);
                        setIsCbAnesSearchPristine(true);
                        setShowAnesMenu(!showAnesMenu);
                      }}
                    />
                    {/* Anestehsiologist serach menu */}
                    {showAnesMenu &&
                      <PopupMenu
                        top={1}
                        left={1}
                        position='absolute'
                        zIndex='3000'
                        visible={showAnesMenu}
                        parent={anesMenuRef}
                        onOutsideClick={() => {
                          setShowAnesMenu(false);
                        }}
                      >
                        <div className={css(styles.gridEllipsisMenuContainer)}>
                          {/* Anesthesiologist search textbox */}
                          <div className='pl-2 pt-1'>
                            <TextInput
                              placeholder='Search Anesthesiologist'
                              value={cbAnesSearch}
                              onChange={handleOnChangeAnesSearch}
                              style={{
                                width: '204px',
                                height: '20px',
                                background: '#103a60',
                                border: '1px solid #60A0E0',
                                borderWidth: '0 0 1px 0',
                                marginBottom: '2px',
                                fontSize: '12px',
                                fontWeight: '700'
                              }}
                            />
                          </div>
                          {cbAnesSearchResults.length === 0 && isCbAnesSearchPristine &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              Type anesthesiologist name in the text box above to search
                            </div>
                          }
                          {cbAnesSearchResults.length === 0 && !isCbAnesSearchPristine && cbAnesSearch.length === 0 &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              Type anesthesiologist name in the text box above to search
                            </div>
                          }
                          {cbAnesSearchResults.length === 0 && !isCbAnesSearchPristine && cbAnesSearch.length > 0 &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              No results found
                            </div>
                          }
                          {cbAnesSearchResults.map((anes) => {
                            return (
                              <div
                                key={`${anes.npi}__surg`}
                                className={css(styles.gridEllipsisMenuItem)}
                                onClick={() => {
                                  setCaseBlockAnes(anes);
                                  setCaseBlockAnesStr(`${anes.firstName.charAt(0)}.${anes.lastName}`);
                                  setShowCaseblockProviderWarning(true);
                                  setShowAnesMenu(false);
                                }}
                              >
                                {`${anes.lastName}, ${anes.firstName}`}
                              </div>
                            );
                          })}
                        </div>
                      </PopupMenu>
                    }
                  </div>
                </div>
                {/* Provider sync warning */}
                {showCaseblockProviderWarning &&
                  <div
                    style={{
                      color: salmon[500],
                      fontSize: '14px',
                      marginTop: '16px'
                    }}
                  >
                    All providers in the case block will be synced to above.
                  </div>
                }
              </div>
              {/* Footer */}
              <div
                className='mt-4 pt-2 fx pr-2'
                style={{
                  height: '44px',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  borderTop: `1px solid ${gray[900]}`
                }}
              >
                {/* Cancel Button */}
                <Button
                  btnType='primary'
                  isOutlineBtn
                  style={{
                    border: `1px solid ${gray[400]}`,
                    color: gray[300],
                    height: '32px',
                    width: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => setIsEditCaseBlockAnes(false)}
                >
                  Cancel
                </Button>
                {/* Save Button */}
                <Button
                  btnType='primary'
                  className='ml-2'
                  style={{
                    height: '32px',
                    width: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                  onClick={() => {
                    setIsEditCaseBlockAnes(false);
                    props.onUpdateBlockAnes(caseBlockAnes, props.id);
                  }}
                >
                  Update
                </Button>
              </div>
            </div>
          </DialogBox>
        }

        {/* Case block - CRNA edit mode */}
        {isEditCaseBlockCrna &&
          <DialogBox
            showCloseBtn={false}
            initWidth={DIALOG_WIDTH}
            top={window.innerWidth <= 576 ? 48 : crnaDialogTop}
            left={window.innerWidth <= 576 ? 8 : crnaDialogLeft}
            initHeight={265}
            isOpen={isEditCaseBlockCrna}
            onClose={() => {
              setIsEditCaseBlockCrna(false);
            }}
            maskBackground="#004080"
            maskOpacity={0}
            closeOnEscKey={false}
            closeOnMaskClick={false}
            header={{
              overrideTheme: true,
              text: 'Edit Case Block CRNA',
              backColor: '#082840',
              foreColor: blue[400],
              closeColor: '#FF6060',
              fontStyle: 'bold',
              fontSize: '14px',
              icon: 'fa fa-pencil'
            }}
          >
            <div
              className='pb-2'
              style={{ minHeight: '120px' }}
            >
              <div className='pl-3 pr-3 pt-2'>
                {/* Case block CRNA */}
                <div
                  className='fx'
                  ref={crnaMenuRef}
                >
                  <div className={css(styles.sideDrawerHeaderRowTitle)}>
                    CRNA
                  </div>
                  <div
                    className='ml-2'
                    style={{
                      color: blue[400],
                      position: 'relative'
                    }}
                  >
                    {caseBlockCrnaStr || 'None'}
                    <i
                      className='ml-1 fa fa-chevron-down'
                      onClick={() => {
                        setCbCrnaSearch('');
                        setCbCrnaSearchResults([]);
                        setIsCbCrnaSearchPristine(true);
                        setShowCrnaMenu(!showCrnaMenu);
                      }}
                    />
                    {/* CRNA serach menu */}
                    {showCrnaMenu &&
                      <PopupMenu
                        top={1}
                        left={1}
                        position='absolute'
                        zIndex='3000'
                        visible={showCrnaMenu}
                        parent={crnaMenuRef}
                        onOutsideClick={() => {
                          setShowCrnaMenu(false);
                        }}
                      >
                        <div className={css(styles.gridEllipsisMenuContainer)}>
                          {/* CRNA search textbox */}
                          <div className='pl-2 pt-1'>
                            <TextInput
                              placeholder='Search CRNA'
                              value={cbCrnaSearch}
                              onChange={handleOnChangeCrnaSearch}
                              style={{
                                width: '204px',
                                height: '20px',
                                background: '#103a60',
                                border: '1px solid #60A0E0',
                                borderWidth: '0 0 1px 0',
                                marginBottom: '2px',
                                fontSize: '12px',
                                fontWeight: '700'
                              }}
                            />
                          </div>
                          {cbCrnaSearchResults.length === 0 && isCbCrnaSearchPristine &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              Type CRNA name in the text box above to search
                            </div>
                          }
                          {cbCrnaSearchResults.length === 0 && !isCbCrnaSearchPristine && cbCrnaSearch.length === 0 &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              Type CRNA name in the text box above to search
                            </div>
                          }
                          {cbCrnaSearchResults.length === 0 && !isCbCrnaSearchPristine && cbCrnaSearch.length > 0 &&
                            <div style={{ fontSize: '14px', padding: '8px', color: gray[300] }}>
                              No results found
                            </div>
                          }
                          {cbCrnaSearchResults.map((crna) => {
                            return (
                              <div
                                key={`${crna.npi}__surg`}
                                className={css(styles.gridEllipsisMenuItem)}
                                onClick={() => {
                                  setCaseBlockCrna(crna);
                                  setCaseBlockCrnaStr(`${crna.firstName.charAt(0)}.${crna.lastName}`);
                                  setShowCaseblockCrnaWarning(true);
                                  setShowCrnaMenu(false);
                                }}
                              >
                                {`${crna.lastName}, ${crna.firstName}`}
                              </div>
                            );
                          })}
                        </div>
                      </PopupMenu>
                    }
                  </div>
                </div>
                {/* Provider sync warning */}
                {showCaseblockCrnaWarning &&
                  <div
                    style={{
                      color: salmon[500],
                      fontSize: '14px',
                      marginTop: '16px'
                    }}
                  >
                    All CRNA in the case block will be synced to above.
                  </div>
                }
              </div>
              {/* Footer */}
              <div
                className='mt-4 pt-2 fx pr-2'
                style={{
                  height: '44px',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  borderTop: `1px solid ${gray[900]}`
                }}
              >
                {/* Cancel Button */}
                <Button
                  btnType='primary'
                  isOutlineBtn
                  style={{
                    border: `1px solid ${gray[400]}`,
                    color: gray[300],
                    height: '32px',
                    width: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => setIsEditCaseBlockCrna(false)}
                >
                  Cancel
                </Button>
                {/* Save Button */}
                <Button
                  btnType='primary'
                  className='ml-2'
                  style={{
                    height: '32px',
                    width: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                  onClick={() => {
                    setIsEditCaseBlockCrna(false);
                    props.onUpdateBlockCrna(caseBlockCrna, props.id);
                  }}
                >
                  Update
                </Button>
              </div>
            </div>
          </DialogBox>
        }
      </div>
    </>
  );
};

GanttCell.defaultProps = {
  type: 'case'
};
export default GanttCell;
